import { PUBLIC_PREVIEW } from "$env/static/public";
import type { GlobalStoryblok } from "$types/components.js";
import { redirect } from "@sveltejs/kit";

export async function load({ url, fetch }) {
  const { pathname, searchParams } = url;

  // Redirect all inconsistently-cased paths to lowercase
  if (/[A-Z]/.test(pathname)) {
    redirect(301, pathname.toLowerCase());
  }

  const redirects = [
    {
      from: "/chipcookies",
      to: "/chip-cookies",
    },
    {
      from: "/GNC",
      to: "/gnc",
    },
    {
      from: "/company",
      to: "/about",
    },
    {
      from: "/articles/zipline-expands-united-states-operations-and-adds-new-customers-in-the-food-retail-and-transportation-sectors",
      to: "/newsroom/news/press-releases/zipline-expands-united-states-operations-and-adds-new-customers-in-the-food-retail-and-transportation-sectors",
    },
    {
      from: "/articles/zipline-unveils-new-autonomous-system-capable-of-quiet-fast-and-precise-home-delivery",
      to: "/newsroom/news/press-releases/zipline-unveils-new-autonomous-system-capable-of-quiet-fast-and-precise-home-delivery",
    },
    {
      from: "/articles/inspiring-the-next-generation-of-drone-operators",
      to: "/newsroom/stories/articles/inspiring-the-next-generation-of-drone-operators",
    },
    {
      from: "/articles/a-first-ever-look-at-the-sustainability-of-autonomous-aerial-logistics",
      to: "/newsroom/stories/research-insights/a-first-ever-look-at-the-sustainability-of-autonomous-aerial-logistics",
    },
    {
      from: "/press-kit",
      to: "/about#press-kit",
    },
    {
      from: "/press",
      to: "/newsroom",
    },
    {
      from: "/newsroom/news/press-releases",
      to: "/newsroom/news/announcements",
    },
    {
      from: "/newsroom/news",
      to: "/newsroom",
    },
    {
      from: "/newsroom/stories",
      to: "/resources/blog",
    },
    {
      from: "/healthcare",
      to: "/solutions/healthcare",
    },
    {
      from: "/restaurants",
      to: "/solutions/restaurants",
    },
    {
      from: "/privacy",
      to: "/privacy-policy",
    },
    {
      from: "/signup",
      to: "/sign-up",
    },
    {
      from: "/sign-up",
      to: "/get-delivery",
    },
    {
      from: "/getdelivery",
      to: "/get-delivery",
    },
    {
      from: "/waitlist",
      to: "/get-delivery",
    },
    {
      from: "/consent",
      to: "https://powerforms.docusign.net/a5909882-2010-4bc8-97bf-53b8b6d2eeef?env=na3&acct=bcbbc2ce-2e2d-4b7c-9147-472b6dc7aa64&accountId=bcbbc2ce-2e2d-4b7c-9147-472b6dc7aa64",
    },
    {
      from: "/ghana-impact-evaluation",
      to: "/newsroom/stories/research-insights/ghana-impact-evaluation",
    },
    {
      from: "/press/zipline-announces-pfizer-partnership",
      to: "/newsroom/news/announcements/zipline-announces-pfizer-partnership",
    },
    {
      from: "/newsroom/stories/articles/our-next-flight-with-walmart",
      to: "/newsroom/news/announcements/our-next-flight-with-walmart-will-reach-millions-of-americans",
    },
    {
      from: "/dallas",
      to: "/texas",
    },
    {
      from: "/newsroom/stories/2023-impact-report",
      to: "/newsroom/stories/impact",
    },
    {
      from: "/get-delivery",
      to: "/",
    },
    {
      from: "/solutions/healthcare",
      to: "/business/solutions/healthcare",
    },
    {
      from: "/solutions/restaurants",
      to: "/business/solutions/restaurants",
    },
    {
      from: "/solutions/convenience",
      to: "/business/solutions/convenience",
    },
    {
      from: "/solutions/public-health",
      to: "/business/solutions/public-health",
    },
    {
      from: "/solutions/farming",
      to: "/business/solutions/farming",
    },
    {
      from: "/resources/videos",
      to: "/business/resources/videos",
    },
    {
      from: "/resources/webinars",
      to: "/business/resources/webinars",
    },
    {
      from: "/resources/guides",
      to: "/business/resources/guides",
    },
    {
      from: "/contact",
      to: "/business/contact",
    },
    {
      from: "/resources/blog",
      to: "/blog",
    },
    {
      from: "/mesquite-launch",
      to: "/mesquite-launch-event",
    },
  ];

  // Redirect all blog posts from /resources/blog to /blog
  if (pathname.startsWith("/resources/blog/")) {
    const tail = pathname.slice("/resources/blog/".length);
    redirect(301, "/blog/" + tail);
  }

  // Redirect all press-releases to announcements
  if (pathname.startsWith("/newsroom/news/press-releases/")) {
    const tail = pathname.slice("/newsroom/news/press-releases/".length);
    redirect(301, "/newsroom/news/announcements/" + tail);
  }

  // Redirect all 2023-impact-report pages to impact
  if (pathname.startsWith("/newsroom/stories/2023-impact-report/")) {
    const tail = pathname.slice("/newsroom/stories/2023-impact-report/".length);
    redirect(301, "/newsroom/stories/impact/" + tail);
  }

  const gh_jid = searchParams.get("gh_jid");
  const jobId = gh_jid ? parseInt(gh_jid) : null;
  if (jobId && !isNaN(jobId)) {
    if (pathname === "/job") {
      redirects.push({
        from: "/job",
        to: `/careers/open-roles/${jobId}`,
      });
    } else if (pathname === "/careers/open-roles") {
      redirects.push({
        from: "/careers/open-roles",
        to: `/careers/open-roles/${jobId}`,
      });
    }
  }

  const redir = redirects.find((r) => r.from === pathname);
  if (redir) {
    redirect(301, redir.to);
  }

  const preview = PUBLIC_PREVIEW === "true";

  const response = await fetch("/api/globals", {
    method: "POST",
    body: JSON.stringify({ preview: preview }),
    headers: {
      "content-type": "application/json",
    },
  });

  const globalsResp = await response.json();
  const globals: { story: { content: GlobalStoryblok } } = globalsResp.data;

  return {
    globals,
    pathname,
  };
}
